import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import HomeTab from "./pages/HomeTab";
import GoalsTab from "./pages/GoalsTab";
import TasksTab from "./pages/TasksTab";
import ContactsTab from "./pages/ContactsTab";
import ContentTab from "./pages/ContentTab";
import SettingsTab from "./pages/SettingsTab";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, hasActiveSubscription } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-violet-600 font-outfit text-xl">Loading...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // If no active subscription, redirect to subscription page
  if (!hasActiveSubscription && location.pathname !== '/subscription' && !location.pathname.startsWith('/subscription/')) {
    return <Navigate to="/subscription" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

const SubscriptionRoute = ({ children }) => {
  const { isAuthenticated, loading, hasActiveSubscription } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-violet-600 font-outfit text-xl">Loading...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // If already has active subscription and not checking payment, redirect to home
  if (hasActiveSubscription && !location.search.includes('session_id')) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-violet-600 font-outfit text-xl">Loading...</div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      
      {/* Subscription Route */}
      <Route path="/subscription" element={<SubscriptionRoute><SubscriptionPage /></SubscriptionRoute>} />
      <Route path="/subscription/success" element={<SubscriptionRoute><SubscriptionPage /></SubscriptionRoute>} />
      
      {/* Protected Routes (require subscription) */}
      <Route path="/" element={<ProtectedRoute><HomeTab /></ProtectedRoute>} />
      <Route path="/goals" element={<ProtectedRoute><GoalsTab /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><TasksTab /></ProtectedRoute>} />
      <Route path="/content" element={<ProtectedRoute><ContentTab /></ProtectedRoute>} />
      <Route path="/contacts" element={<ProtectedRoute><ContactsTab /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsTab /></ProtectedRoute>} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
